import { logWarn } from '@ardoq/logging';
import { pick } from 'lodash';
import { Method, toRequestOptions } from './api';
import { HTTPStatusCode } from '@ardoq/api-types';

type Event = {
  name: string;
  metadata: any;
  client: string;
  context: {
    userAgent: string;
    page: {
      path: string;
      referrer: string;
    };
  };
};

const MAX_LOGGED_RESPONSE_BODY_LENGTH = 500;

// Tracking is best-effort analytics, so we don't log failures the client cannot
// act on: auth/permission (anonymous viewer or no access), proxy auth (a
// corporate proxy), and transient upstream errors (bad gateway / unavailable /
// timeout, e.g. during a deploy or org maintenance). Statuses that indicate we
// sent a bad event — such as 400 — are still surfaced.
const NON_ACTIONABLE_TRACKING_STATUSES: ReadonlySet<number> = new Set([
  HTTPStatusCode.UNAUTHORIZED,
  HTTPStatusCode.FORBIDDEN,
  HTTPStatusCode.PROXY_AUTHENTICATION_REQUIRED,
  HTTPStatusCode.BAD_GATEWAY,
  HTTPStatusCode.SERVICE_UNAVAILABLE,
  HTTPStatusCode.GATEWAY_TIMEOUT,
]);

export const isExpectedResponse = (response: Response): boolean =>
  response.ok || NON_ACTIONABLE_TRACKING_STATUSES.has(response.status);

// statusText is empty on HTTP/2, which left the logged error with an empty
// message ("No error message" in Sentry). Fall back to the status code.
export const getTrackingErrorMessage = (
  status: number,
  statusText: string
): string => statusText || `Failed to track event (HTTP ${status})`;

// The backend's rejection reason for a bad event lives in the response body;
// statusText alone (e.g. "Bad Request") is not actionable. Best-effort and
// truncated — surfacing the diagnostic must never throw or flood the log.
export const readResponseBodyForLog = async (
  response: Response
): Promise<string | undefined> => {
  try {
    const body = await response.text();
    return body ? body.slice(0, MAX_LOGGED_RESPONSE_BODY_LENGTH) : undefined;
  } catch {
    return undefined;
  }
};

const logTrackingError = async (
  event: Event,
  response: Response
): Promise<void> => {
  const responseBody = await readResponseBodyForLog(response);
  const error = new Error(
    getTrackingErrorMessage(response.status, response.statusText)
  );
  logWarn(error, 'Failed to track event', {
    name: event.name,
    responseBody,
    ...pick(response, ['ok', 'status', 'statusText']),
  });
};

const trackEvent = async (event: Event): Promise<void> => {
  const response = await fetch(
    new Request('/api/track/event', toRequestOptions(Method.POST, event))
  );
  if (!isExpectedResponse(response)) {
    await logTrackingError(event, response);
  }
};

const trackEarlyEvent = async (event: Event): Promise<void> => {
  const response = await fetch(
    new Request('/api/track-early/event', toRequestOptions(Method.POST, event))
  );
  if (!isExpectedResponse(response)) {
    await logTrackingError(event, response);
  }
};

export const trackApi = {
  trackEvent,
  trackEarlyEvent,
};
